// ORCA360 - Login Preference Toggle Hook
// Manages the mobile login destination preference (Sessions vs Dashboard)
// with safe timer cleanup to prevent state updates on unmounted components.

import { useState, useEffect, useCallback } from 'react';
import { MOBILE_REDIRECT_OPT_OUT_KEY } from '../utils/device';

interface LoginPreferenceToggle {
  /** true when the user has opted to land on Dashboard instead of Sessions */
  prefersDashboard: boolean;
  /** Toggle the preference and show a toast */
  handleToggle: () => void;
  /** Current toast message (empty string when hidden) */
  toastMessage: string;
}

export function useLoginPreferenceToggle(): LoginPreferenceToggle {
  const [prefersDashboard, setPrefersDashboard] = useState(
    () => typeof window !== 'undefined'
      && localStorage.getItem(MOBILE_REDIRECT_OPT_OUT_KEY) === 'true',
  );
  const [toastMessage, setToastMessage] = useState('');

  // Auto-dismiss toast with cleanup to avoid memory leaks
  useEffect(() => {
    if (!toastMessage) return;
    const timer = setTimeout(() => setToastMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  const handleToggle = useCallback(() => {
    if (prefersDashboard) {
      localStorage.removeItem(MOBILE_REDIRECT_OPT_OUT_KEY);
      setPrefersDashboard(false);
      setToastMessage('Next login will open Sessions.');
    } else {
      localStorage.setItem(MOBILE_REDIRECT_OPT_OUT_KEY, 'true');
      setPrefersDashboard(true);
      setToastMessage('Next login will open Dashboard.');
    }
  }, [prefersDashboard]);

  return { prefersDashboard, handleToggle, toastMessage };
}
